import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { GlassCard } from './GlassCard';

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  gradient: string;
  delay?: number;
}

const FeatureCardComponent: React.FC<FeatureCardProps> = ({
  icon: Icon,
  title,
  description,
  color,
  gradient,
  delay = 0
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ 
        duration: 0.6, 
        delay,
        type: "spring",
        stiffness: 100
      }}
      className="group h-full"
    >
      <motion.div
        whileHover={{ 
          scale: 1.02,
          y: -3
        }}
        transition={{ 
          type: "spring", 
          stiffness: 400, 
          damping: 25,
          mass: 0.5
        }}
        className="h-full"
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)'
        }}
      >
        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
          {/* Subtle hover background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
          
          {/* Icon section */}
          <div className="flex items-center gap-4 mb-6">
            <motion.div 
              className={`p-4 bg-${color}-500/15 rounded-xl border border-${color}-400/20 relative`}
              whileHover={{ 
                rotate: [0, -3, 3, 0],
                scale: 1.05
              }}
              transition={{ 
                duration: 0.3,
                ease: "easeOut"
              }}
              style={{
                willChange: 'transform',
                transformOrigin: 'center'
              }}
            >
              {/* Icon glow effect */}
              <div className={`absolute inset-0 bg-${color}-400/10 rounded-xl blur-sm group-hover:bg-${color}-400/20 transition-colors duration-300`} />
              <Icon className={`w-8 h-8 text-${color}-400 relative z-10 group-hover:text-${color}-300 transition-colors duration-300`} />
            </motion.div>
            
            <motion.h3 
              className="text-xl font-bold text-white group-hover:text-gray-100 transition-colors duration-300"
              whileHover={{ x: 3 }}
            >
              {title}
            </motion.h3>
          </div>
          
          <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
            {description}
          </p>
          
          {/* Subtle corner accent */}
          <div className={`absolute top-4 right-4 w-2 h-2 bg-${color}-400/30 rounded-full group-hover:bg-${color}-400/50 transition-colors duration-300`} />
        </GlassCard>
      </motion.div>
    </motion.div>
  );
};

export const FeatureCard = memo(FeatureCardComponent);
